import { Box, Typography } from '@mui/material';
import { ReactFlow, Background, Controls, Node, Edge } from '@xyflow/react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { capitalize } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { sharedColors, sharedStyles } from '../../utils/Style';
import { SynchronizationSummary } from '../../interfaces/SynchronizationSummary/SynchronizationSummary';
import { getSynchronizationSummary } from '../../api/SynchronizationSummary';
import { Waiting } from '../../components/Waiting/Waiting';
import { availablePartnerPlatforms } from '../../constants/PartnerPlatform';
import { CentralNode, CustomNode } from './CustomNode';
import {
  distributeVertically,
  getPartnerPlatformEnablements,
  getPartnerPlatformMatchCounts,
  getPartnerPlatformStatuses,
} from './utils';
import { PartnerPlatform } from '../../interfaces/PartnerPlatform/PartnerPlatform';
import { SynchronizationSummaryController } from './SynchronizationSummaryController';
import { formatLocalTime } from '../../utils/Date';

const CENTRAL_NODE_ID = 'nnet';

export const SynchronizationSummaryPage = () => {
  const navigate = useNavigate();

  const [summary, setSummary] = useState<SynchronizationSummary | undefined>(
    undefined,
  );
  const [statusMap, setStatusMap] = useState<
    Record<PartnerPlatform, boolean> | undefined
  >(undefined);
  const [matchCountMap, setMatchCountMap] = useState<
    Record<PartnerPlatform, number> | undefined
  >(undefined);
  const [enablementMap, setEnablementMap] = useState<
    Record<PartnerPlatform, boolean> | undefined
  >(undefined);
  const [loadingSummary, setLoadingSummary] = useState(false);

  const refreshSummary = () => {
    setLoadingSummary(true);
    getSynchronizationSummary()
      .then(setSummary)
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingSummary(false));
  };

  const refreshMatchCountMap = () => {
    setMatchCountMap(undefined);
    getPartnerPlatformMatchCounts()
      .then(setMatchCountMap)
      .catch((err) => toast.error(err.message));
  };

  const refreshStatusMap = () => {
    setStatusMap(undefined);
    getPartnerPlatformStatuses().then(setStatusMap);
  };

  const refreshEnablementMap = () => {
    setEnablementMap(undefined);
    getPartnerPlatformEnablements()
      .then(setEnablementMap)
      .catch((err) => toast.error(err.message));
  };

  useEffect(() => {
    refreshSummary();
    refreshStatusMap();
    refreshMatchCountMap();
    refreshEnablementMap();
  }, []);

  const handleClick = (platform: PartnerPlatform) => {
    navigate(`/products/synchronization-summary/${platform}`);
  };

  const handleNodeClick = useCallback(
    (_: React.MouseEvent, node: Node) => {
      // Sadece marketplace node'ları için çalışsın, NNET'e tıklayınca bi şey yapma
      if (availablePartnerPlatforms.includes(node.id as PartnerPlatform)) {
        handleClick(node.id as PartnerPlatform);
      }
    },
    [handleClick],
  );

  const handleEdgeClick = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      // Edge'lerde target hep platform, source NNET
      if (availablePartnerPlatforms.includes(edge.target as PartnerPlatform)) {
        handleClick(edge.target as PartnerPlatform);
      }
    },
    [handleClick],
  );

  const buildSyncNodes = useCallback(() => {
    const centerNNET = { x: 0, y: 0 };

    const positions = distributeVertically(
      500,
      0,
      120,
      availablePartnerPlatforms.length,
    );

    const centralNode = {
      id: CENTRAL_NODE_ID,
      type: 'centralNode',
      position: centerNNET,
      data: {
        label: 'NNET',
        platform: 'internal',
        productCount: summary?.internalProductCount,
      },
    };

    const marketplaceNodes = availablePartnerPlatforms.map(
      (platform, index) => ({
        id: platform,
        type: 'customNode',
        position: positions[index],
        data: {
          label: capitalize(platform),
          platform,
          productCount: summary?.platformCounts[platform],
          status: statusMap?.[platform],
          enablement: enablementMap?.[platform],
        },
      }),
    );

    return [centralNode, ...marketplaceNodes];
  }, [summary, statusMap, enablementMap]);

  const buildSyncEdges = useCallback(() => {
    return availablePartnerPlatforms.map((platform) => {
      const status = statusMap?.[platform];
      const enablement = enablementMap?.[platform];
      const matchCount = matchCountMap?.[platform];
      const isConnected = status === true;

      const label =
        matchCount === undefined || !status || !enablement
          ? undefined
          : `${matchCount} ürün`;

      return {
        id: `edge-nnet-${platform}`,
        source: CENTRAL_NODE_ID,
        target: platform,
        animated: isConnected && !!matchCount,
        label,
        labelBgBorderRadius: 4,
        labelBgStyle: { fill: '#fff' },

        style: {
          stroke:
            status === undefined
              ? '#bdbdbd'
              : status
              ? enablement
                ? '#2e7d32'
                : sharedColors.statusOrange
              : '#c62828',
          strokeWidth: 2.5,
        },
      };
    });
  }, [statusMap, matchCountMap, enablementMap]);

  const nodes = buildSyncNodes();

  const edges = buildSyncEdges();

  return (
    <Box sx={{ ...sharedStyles.pageBody }}>
      <Typography variant='h5' sx={{ ...sharedStyles.pageTitle, mb: 0.5 }}>
        Eşitleme Özeti
      </Typography>
      {!!summary && (
        <Typography sx={{ fontSize: '14px', color: sharedColors.gray5 }}>
          Son güncelleme: {formatLocalTime(summary.lastUpdated)}
        </Typography>
      )}
      <Waiting open={loadingSummary} />

      <SynchronizationSummaryController
        onRefreshEnablement={refreshEnablementMap}
        onRefreshStatus={refreshStatusMap}
      >
        <Box sx={{ flexGrow: 1, width: '100%' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={{ centralNode: CentralNode, customNode: CustomNode }}
            fitView
            fitViewOptions={{
              padding: 0.3,
              minZoom: 0.5,
              maxZoom: 1.2,
            }}
            nodesDraggable={false}
            nodesConnectable={false}
            onNodeClick={handleNodeClick}
            onEdgeClick={handleEdgeClick}
          >
            <Background />
            <Controls />
          </ReactFlow>
        </Box>
      </SynchronizationSummaryController>
    </Box>
  );
};
